import React, { useState } from 'react';
import { Link, Loader2, Check } from 'lucide-react';
import { cn } from '../../utils/cn';
import { useLanguage } from '../../contexts/LanguageContext';
import { referencesApi } from '../../api/references';

interface CopyLinkButtonProps {
  referenceId: string;
  className?: string;
  disabled?: boolean;
}

const CopyLinkButton = ({ referenceId, className, disabled = false }: CopyLinkButtonProps) => {
  const { t } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const handleCopyLink = async () => {
    if (disabled) return;
    try {
      setIsLoading(true);
      const { url } = await referencesApi.getFormUrl(referenceId);
      await navigator.clipboard.writeText(url);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy link:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      onClick={handleCopyLink}
      disabled={isLoading || disabled}
      className={cn(
        "inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs",
        "text-indigo-700 bg-indigo-50 border border-indigo-200",
        "hover:bg-indigo-100 transition-colors",
        isLoading && "opacity-70 cursor-not-allowed",
        disabled && "opacity-50 cursor-not-allowed bg-gray-50 text-gray-500 hover:bg-gray-50 border-gray-200",
        className
      )}
      title={disabled ? t('candidates.copyLink.disabled') : t('candidates.copyLink.title')}
    >
      {isLoading ? (
        <Loader2 className="w-3 h-3 animate-spin" />
      ) : isCopied ? (
        <Check className="w-3 h-3" />
      ) : (
        <Link className="w-3 h-3" />
      )}
      <span>
        {isLoading 
          ? t('common.loading') 
          : isCopied 
            ? t('candidates.copyLink.copied') 
            : t('candidates.copyLink.label')}
      </span>
    </button>
  );
};

export default CopyLinkButton;